"use client";

import type { EvmChainConfig } from "@/components/evm-chain-selector-menu";

// EIP-1193 compatible provider interface - accepts any object with a request method.
// Exported so callers can cast strict viem providers to this looser type.
export interface EIP1193ProviderLike {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}

interface SwitchNetworkError extends Error {
  code?: number;
}

// Testnet configs keep the mainnet name, which wallets reject when adding the testnet chain ID.
const TESTNET_CHAIN_NAMES: Record<number, string> = {
  11155111: "Sepolia",
  421614: "Arbitrum Sepolia",
  84532: "Base Sepolia",
};

/**
 * Attempts to switch the connected EVM wallet to the specified chain.
 * If the chain is not known to the wallet, tries to add it first.
 *
 * @param provider - The EIP1193 provider from the connected wallet
 * @param chainConfig - The target chain configuration
 * @throws Error if the user rejects the switch or addition
 */
export async function switchEvmNetwork(
  provider: EIP1193ProviderLike,
  chainConfig: EvmChainConfig,
): Promise<void> {
  const hexChainId = `0x${chainConfig.chainId.toString(16)}`;

  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: hexChainId }],
    });
  } catch (error) {
    const switchError = error as SwitchNetworkError;

    // Error code 4902 means the chain is not added to the wallet
    if (switchError.code === 4902) {
      await provider.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: hexChainId,
            chainName: TESTNET_CHAIN_NAMES[chainConfig.chainId] ?? chainConfig.name,
            nativeCurrency: {
              name: chainConfig.currencySymbol,
              symbol: chainConfig.currencySymbol,
              decimals: 18,
            },
            rpcUrls: [chainConfig.rpcUrl],
            blockExplorerUrls: [chainConfig.blockExplorer],
          },
        ],
      });

      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexChainId }],
      });
    } else {
      throw error;
    }
  }
}
